/**
 * SignalR client stream with raw message logging
 */

import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import {
  SIGNALR_CONFIG,
  AUTH_HEADERS,
  CONNECTION_STATUS,
} from "../config/signalr";
import type {
  ConnectionCredentials,
  ConnectionStatus,
} from "../../shared/types";
import type {
  TradeHistoryBatch,
  HistorySyncSource,
  HistoryBatchMode,
} from "../../trade-history/types";
import type {
  OpenedTradesResponse,
  OrderModifyResponse,
  OrderProgress,
  OrderUpdate,
} from "../../opened-trades/types";
import {
  VybeMsg,
  VybeTradeType,
  formatTradeStatus,
  formatTradeType,
  formatUpdateType,
} from "../../opened-trades/types";
import type { ConnectionProgressBatch } from "../../connection-progress/types";
import { useRawMessagesStore } from "../../messaging/stores/useRawMessagesStore";
import {
  INVESTOR_READ_ONLY_MESSAGE,
  isInvestorAccount,
} from "../../account-information/hooks/useAccountInformation";
import { getVybeToken } from "@/lib/vybe-auth/store";
import { useSettingsStore } from "@/lib/settings";
import {
  useJournalStore,
  journalSignalRIncoming,
  journalSignalROutgoing,
} from "../../messaging/stores/useJournalStore";
import { toast } from "sonner";
import { playNotificationSound } from "@/lib/sounds";
import {
  HubConnectionClosedError,
  isHubConnectionClosedError,
} from "../utils/hubErrors";

type HistoryResponseMeta = {
  syncSource: HistorySyncSource;
  batchMode: HistoryBatchMode;
};

export interface VybeOrderClosePayload {
  Ticket: number;
  Symbol: string;
  Price: number;
  Lots: number;
  OrderType: number;
  Slippage?: number;
  CloseByTicket?: number;
}

export class VybeTraderClientStream {
  private connection: HubConnection | null = null;
  private connectionStatus: ConnectionStatus = CONNECTION_STATUS.DISCONNECTED;
  private retryCount = 0;

  /** Pairs with each `GetOrderHistory` / `GetPendingOrderHistory` response (FIFO). */
  private historyResponseMetaQueue: HistoryResponseMeta[] = [];
  private historyResyncTimer: ReturnType<typeof setTimeout> | null = null;
  private openTradesResyncTimer: ReturnType<typeof setTimeout> | null = null;
  /** True while `disconnect()` is stopping the hub (suppresses invoke cancellation noise). */
  private intentionalDisconnect = false;
  /** Epoch ms when the hub started reconnecting (start of the data gap to backfill). */
  private reconnectingSince: number | null = null;
  /** True once a connection has succeeded, so a later connect() is a manual reconnect. */
  private hasConnectedBefore = false;
  /** Last credentials used by connect() — reused by recycle() after a suspect wake. */
  private lastCredentials: ConnectionCredentials | null = null;
  /**
   * Latched when the server sends a VybeProgressType.Error progress frame
   * (e.g. rejected login). While latched, automatic reconnects are capped at
   * MAX_LOGIN_REJECTED_RETRIES — retrying bad credentials forever is pointless.
   * Cleared by a successful (re)connect or a success progress frame.
   */
  private loginRejected = false;
  /** Six spaced attempts (~62s) — longer than the gateway holds a stale session for. */
  private static readonly MAX_LOGIN_REJECTED_RETRIES = 6;

  // Cache dla deduplikacji quotesów
  private lastQuoteCache = new Map<string, { bid: number; ask: number; last: number; time: string; volume?: number }>();
  private lastQuoteUpdate = new Map<string, number>(); // timestamp cache
  /**
   * Per-symbol epoch ms of the last quote actually applied to the store. The
   * monotonic tick gate rejects any quote whose `time` is <= this, so a burst of
   * replayed pre-sleep frames can never repaint stale prices (proposal 23).
   */
  private lastAppliedTickTime = new Map<string, number>();

  private updateQuoteStore(data: unknown): void {
    import("../../subscribed-symbols/stores/useSubscribedSymbolsStore").then(
      ({ useSubscribedSymbolsStore }) => {
        if (this.isQuoteData(data)) {
          const quote = data as any;
          const lastQuote = this.lastQuoteCache.get(quote.symbol);

          // Fast pre-check: skip byte-identical quotes (unchanged dedup).
          if (lastQuote &&
              lastQuote.bid === quote.bid &&
              lastQuote.ask === quote.ask &&
              lastQuote.last === quote.last &&
              lastQuote.time === quote.time &&
              lastQuote.volume === quote.volume) {
            return; // Skip identical quote
          }

          // Monotonic tick gate: drop any quote whose timestamp is older than or
          // equal to the last one we applied for this symbol. This is what stops
          // the replayed burst of buffered pre-sleep frames from repainting stale
          // prices after the machine wakes, and also hardens the normal path
          // against out-of-order frames.
          const tickMs = this.parseQuoteTime(quote.time);
          if (tickMs !== null) {
            const lastMs = this.lastAppliedTickTime.get(quote.symbol);
            if (lastMs !== undefined && tickMs <= lastMs) {
              this.noteDroppedStaleTick(quote.symbol);
              return; // Replayed / out-of-order tick — reject.
            }
          }

          this.lastQuoteCache.set(quote.symbol, {
            bid: quote.bid,
            ask: quote.ask,
            last: quote.last,
            time: quote.time,
            volume: quote.volume
          });
          this.lastQuoteUpdate.set(quote.symbol, Date.now());
          if (tickMs !== null) {
            this.lastAppliedTickTime.set(quote.symbol, tickMs);
            void this.noteAppliedTickTime(quote.symbol, tickMs);
          }

          useSubscribedSymbolsStore.getState().updateQuote(quote);
        }
      }
    );
  }

  /** Parse a quote `time` (ISO string or epoch) to epoch ms, or null if unparseable. */
  private parseQuoteTime(time: unknown): number | null {
    if (typeof time === "number" && Number.isFinite(time)) return time;
    if (typeof time === "string") {
      const ms = Date.parse(time);
      return Number.isNaN(ms) ? null : ms;
    }
    return null;
  }

  /** Count of stale ticks rejected by the monotonic gate (telemetry / debugging). */
  private droppedStaleTicks = 0;
  private noteDroppedStaleTick(symbol: string): void {
    this.droppedStaleTicks += 1;
    // Log the first few + then every 25th so a wake burst is visible without spam.
    if (this.droppedStaleTicks <= 3 || this.droppedStaleTicks % 25 === 0) {
      this.logJournal(
        `Tick gate: dropped stale/out-of-order quote for ${symbol} (total ${this.droppedStaleTicks})`,
        "info"
      );
    }
  }

  public getDroppedStaleTickCount(): number {
    return this.droppedStaleTicks;
  }

  private noteAppliedTickTime(symbol: string, epochMs: number): Promise<void> {
    return import("../stores/useConnectionFreshnessStore")
      .then(({ useConnectionFreshnessStore }) => {
        useConnectionFreshnessStore.getState().noteAppliedTick(symbol, epochMs);
      })
      .catch(() => {
        /* store unavailable — ignore */
      });
  }

  public async connect(credentials: ConnectionCredentials): Promise<void> {
    if (this.connectionStatus === CONNECTION_STATUS.CONNECTED) {
      throw new Error("Already connected");
    }

    if (this.connectionStatus === CONNECTION_STATUS.CONNECTING) {
      throw new Error("Connection in progress");
    }

    this.setConnectionStatus(CONNECTION_STATUS.CONNECTING);
    this.logJournal("Connecting to SignalR...", "info");
    // Remember the credentials so recycle() can hard-reconnect the same session
    // after a suspect wake without the caller re-supplying them.
    this.lastCredentials = credentials;

    // Reset connection progress for new connection attempt
    this.resetConnectionProgress();

    try {
      const connectionUrl = this.buildConnectionUrl(credentials);

      this.connection = new HubConnectionBuilder()
        .withUrl(connectionUrl, {
          transport: 1, // WebSocket only
          skipNegotiation: true,
          accessTokenFactory: () => "", // Add empty token factory
          withCredentials: false, // Ensure no credentials are sent
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // After a rejected login, cap the pointless retries and leave the
            // account flagged invalid (returning null stops auto-reconnect).
            if (
              this.loginRejected &&
              retryContext.previousRetryCount >=
                VybeTraderClientStream.MAX_LOGIN_REJECTED_RETRIES
            ) {
              this.logJournal(
                "Auto-reconnect stopped after repeated login rejections",
                "error"
              );
              return null;
            }
            // After a rejected login the gateway emits the same Error frame for "broker unreachable"
            // and for a login the engine refused because the PREVIOUS page's session was still held
            // (a PWA refresh dials within a second; the old socket is reaped after the keepalive /
            // 40s server timeout). Space those retries out so the budget outlasts that window
            // instead of burning every attempt inside it.
            if (this.loginRejected) {
              const spaced = [2000, 5000, 10000, 15000, 15000, 15000];
              return spaced[Math.min(retryContext.previousRetryCount, spaced.length - 1)];
            }
            // Retry immediately, then back off (0 → 1s → 2s → 5s). The previous flat 5s delay applied
            // to the FIRST attempt too, so every drop — including a mobile resume from suspend — paid a
            // guaranteed 5s floor before anything was even tried (SR Global 2026-09-08).
            const backoff = [0, 1000, 2000, SIGNALR_CONFIG.RETRY_DELAY_MS];
            return backoff[Math.min(retryContext.previousRetryCount, backoff.length - 1)];
          },
        })
        // Detect a genuinely dead / half-open socket in seconds rather than the
        // 30s SignalR default (proposal 23) — the client pings every KEEPALIVE_MS
        // and gives up if the server is silent for SERVER_TIMEOUT_MS.
        .withServerTimeout(SIGNALR_CONFIG.SERVER_TIMEOUT_MS)
        .withKeepAliveInterval(SIGNALR_CONFIG.KEEPALIVE_MS)
        .configureLogging("Information") // Reduce logging level
        .build();

      this.setupEventHandlers();
      this.setupRawMessageLogging();

      await this.connection.start();

      this.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
      this.retryCount = 0;
      this.loginRejected = false;
      this.logJournal("Connected successfully", "info");
      void this.trySyncEconomicCalendarSession();
      void this.trySyncNewsSession();

      // A fresh connect() after a prior session is a manual reconnect (the hub's
      // automatic reconnect gave up and closed, then something reconnected us).
      // Resync market data so the watchlist's cached 1m OHLC isn't left stale.
      // The very first connect is skipped — initial load is driven by the
      // watchlist/chart init flows.
      if (this.hasConnectedBefore) {
        this.resyncMarketDataAfterReconnect();
      }
      this.hasConnectedBefore = true;

      // Send a test message to verify connection is working
      setTimeout(() => {
        this.logRawMessage("SYSTEM", "ConnectionTest", {
          status: "connected",
          timestamp: new Date(),
        });
      }, 1000);
    } catch (error) {
      this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      this.connection = null;
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        this.logJournal("Connection stopped during disconnect", "info");
        throw new HubConnectionClosedError(error);
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      this.logJournal(`Connection failed: ${message}`, "error");
      throw error;
    }
  }

  public async disconnect(): Promise<void> {
    // Allow disconnect from any state except already disconnected
    if (this.connectionStatus === CONNECTION_STATUS.DISCONNECTED) {
      return;
    }

    this.logJournal(
      `Disconnecting from state: ${this.connectionStatus}`,
      "info"
    );

    // If we're in CONNECTING state, we need to force stop
    if (this.connectionStatus === CONNECTION_STATUS.CONNECTING) {
      this.logJournal("Force stopping connection attempt...", "info");
    }

    if (this.connection) {
      this.intentionalDisconnect = true;
      try {
        await this.connection.stop();
      } catch (error) {
        if (!isHubConnectionClosedError(error)) {
          const message =
            error instanceof Error ? error.message : "Unknown error";
          this.logJournal(`Disconnect error: ${message}`, "error");
        }
      } finally {
        this.clearHistoryResyncScheduling();
        this.connection = null;
        this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        this.retryCount = 0;
        this.intentionalDisconnect = false;
        // Intentional disconnect: the next connect() is a fresh session (e.g.
        // account switch), not a reconnect — let the init flows load it.
        this.hasConnectedBefore = false;
        this.lastCredentials = null;
        this.logJournal("Disconnected", "info");
      }
    } else {
      // Even if connection object is null, reset the status
      this.clearHistoryResyncScheduling();
      this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      this.retryCount = 0;
      this.hasConnectedBefore = false;
      this.lastCredentials = null;
      this.logJournal("Disconnected (no active connection)", "info");
    }
  }

  /**
   * Hard-recycle the hub after a suspect wake (proposal 23). The socket may look
   * "connected" but be half-open with a buffer of stale pre-sleep frames queued.
   * We force-stop it (flushing those buffered handlers with the dead connection)
   * and connect() fresh with the same credentials, then let the caller run the
   * snapshot-resync. No-op if we were never connected (nothing to recover).
   */
  public async recycle(): Promise<void> {
    const credentials = this.lastCredentials;
    if (!credentials) {
      this.logJournal("Recycle skipped: no active session to recover", "info");
      return;
    }

    this.logJournal("Recycling suspect connection (wake watchdog)...", "info");

    // Mark this stop as intentional so the teardown noise (invoke cancellations,
    // onclose) is suppressed, but preserve credentials so we can immediately
    // reconnect. Reuse the buffered-frame-flushing stop() from the hub.
    if (this.connection) {
      this.intentionalDisconnect = true;
      try {
        await this.connection.stop();
      } catch (error) {
        if (!isHubConnectionClosedError(error)) {
          const message =
            error instanceof Error ? error.message : "Unknown error";
          this.logJournal(`Recycle stop error: ${message}`, "warning");
        }
      } finally {
        this.clearHistoryResyncScheduling();
        this.connection = null;
        this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        this.intentionalDisconnect = false;
      }
    }

    // Drop the tick-gate baseline for the recycled session so the fresh snapshot
    // can repaint (its timestamps are "new" relative to a cleared gate), while
    // the snapshot-resync still refills authoritative state.
    this.lastAppliedTickTime.clear();

    // A fresh connect() with hasConnectedBefore=true triggers the built-in
    // resyncMarketDataAfterReconnect() path on success. We keep credentials so
    // connect() uses them and re-arms lastCredentials.
    this.hasConnectedBefore = true;
    await this.connect(credentials);
  }

  /**
   * Public entry point for the snapshot-resync (proposal 23). Callers on the
   * wake path use this after recycle() to refill fresh quotes / OHLC / positions;
   * internally this is the same path the automatic reconnect uses.
   */
  public resyncMarketData(): void {
    this.resyncMarketDataAfterReconnect();
  }

  public async send(method: string, data?: unknown): Promise<void> {
    if (!this.connection || !this.isConnected()) {
      throw new Error("Not connected");
    }

    try {
      this.logRawMessage("OUTGOING", method, data);

      if (data) {
        await this.connection.invoke(method, data);
      } else {
        await this.connection.invoke(method);
      }
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        throw new HubConnectionClosedError(error);
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Send failed: ${message}`);
    }
  }

  /**
   * Request/response: DNS-resolve + TCP-ping a broker endpoint via the gateway
   * (`ResolveEndpoint` hub method, proposal 19 Edit-page lookup).
   */
  public async resolveEndpoint(
    host: string,
    port: number
  ): Promise<{ success: boolean; ip?: string; port: number; latencyMs: number; errorMessage?: string }> {
    if (!this.connection || !this.isConnected()) {
      throw new Error("Not connected");
    }
    this.logRawMessage("OUTGOING", "ResolveEndpoint", { host, port });
    return (await this.connection.invoke("ResolveEndpoint", { host, port })) as {
      success: boolean;
      ip?: string;
      port: number;
      latencyMs: number;
      errorMessage?: string;
    };
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  public isConnected(): boolean {
    return this.connectionStatus === CONNECTION_STATUS.CONNECTED;
  }

  private buildConnectionUrl(credentials: ConnectionCredentials): string {
    // const url = new URL("https://signalr.hft-app.net/TradingClientHub");

    // Route to the account's gateway hub when provided; otherwise the single default hub.
    const baseUrl = credentials.hubUrl || SIGNALR_CONFIG.HUB_URL;
    const url = new URL(`${baseUrl}/TradingClientHub`);

    // Single-connection auth (MT5 parity): no stored password → present the CRM session token
    // instead and let the GATEWAY resolve (and first-time provision) the credential server-side.
    // The middleware's token branch only fires when the login/password params are absent, so the
    // two schemes never mix on one request.
    if (!credentials.password) {
      const sessionToken = getVybeToken();
      if (sessionToken) {
        url.searchParams.append(AUTH_HEADERS.VYBE_TOKEN, sessionToken);
        // Name the account we actually want. Without this the gateway can only ask the CRM for
        // "an account this session owns", which answers with the lowest-numbered one — so
        // connecting login 29 would silently log in login 24. The gateway re-checks ownership
        // against the session before honouring it, so this is a request, not an assertion.
        if (credentials.login) {
          url.searchParams.append(
            AUTH_HEADERS.VYBE_LOGIN,
            credentials.login.toString()
          );
        }
        if (
          credentials.provider !== undefined &&
          credentials.provider !== 0
        ) {
          url.searchParams.append(
            AUTH_HEADERS.TRADING_PROVIDER,
            credentials.provider.toString()
          );
        }
        return url.toString();
      }
    }

    // Build the credential query manually with encodeURIComponent rather than URLSearchParams.
    // URLSearchParams serializes as application/x-www-form-urlencoded, which encodes a space as
    // "+"; the gateway reads these off the URI query via ASP.NET HttpRequest.Query, which decodes
    // percent-escapes but does NOT turn "+" back into a space. A password containing a space (or a
    // literal "+") therefore arrived at the broker corrupted — the account authorized but the
    // password step failed. encodeURIComponent emits "%20"/"%2B", which Request.Query decodes back
    // exactly, so every character class round-trips. (Alphanumerics and @/! were never affected.)
    const params = [
      [AUTH_HEADERS.VYBE_LOGIN, credentials.login.toString()],
      [AUTH_HEADERS.VYBE_PASSWORD, credentials.password],
      [AUTH_HEADERS.VYBE_HOST, credentials.host],
      [AUTH_HEADERS.VYBE_PORT, credentials.port.toString()],
    ];
    if (credentials.provider !== undefined && credentials.provider !== 0) {
      params.push([AUTH_HEADERS.TRADING_PROVIDER, credentials.provider.toString()]);
    }
    url.search = params
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");

    return url.toString();
  }

  /**
   * Push the real socket state into the connection-progress store so the
   * header dot reflects reality instead of the last latched progress frame.
   */
  private syncSocketStateToProgressStore(
    state: "connected" | "connecting" | "disconnected" | "error"
  ): void {
    import("../../connection-progress/stores/useConnectionProgressStore")
      .then(({ useConnectionProgressStore }) => {
        useConnectionProgressStore.getState().setSocketState(state);
      })
      .catch(() => {
        /* ignore if module unavailable */
      });
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;
    // Handlers are per-HubConnection but mutate the single shared slot. A recycle() that raced
    // SignalR's own reconnect loop left the OLD connection's late onclose nulling out the NEW,
    // working one and painting it disconnected — so every handler first checks it still owns the
    // slot.
    const owner = this.connection;
    const stale = () => this.connection !== null && this.connection !== owner;

    this.connection.onclose(async (error) => {
      if (stale()) return;
      this.reconnectingSince = null;
      this.clearHistoryResyncScheduling();
      this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      this.syncSocketStateToProgressStore(error ? "error" : "disconnected");
      const message = error
        ? `Connection closed: ${error.message}`
        : "Connection closed";
      this.logJournal(message, error ? "error" : "info");
      this.connection = null;
    });

    this.connection.onreconnecting(() => {
      if (stale()) return;
      if (this.reconnectingSince === null) {
        this.reconnectingSince = Date.now();
      }
      this.setConnectionStatus(CONNECTION_STATUS.RECONNECTING);
      this.syncSocketStateToProgressStore("connecting");
      this.logJournal("Reconnecting...", "info");
    });

    this.connection.onreconnected(() => {
      if (stale()) return;
      this.loginRejected = false;
      this.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
      this.syncSocketStateToProgressStore("connected");
      this.logJournal("Reconnected successfully", "info");
      void this.trySyncEconomicCalendarSession();
      void this.trySyncNewsSession();
      // A resumed hub connection does NOT re-run the login handshake, so the gateway never re-pushes
      // VybeOnAccountRec — and nothing else ever asks for it. If the account store was cleared while
      // the socket was down (the provider resets it on a connected→disconnected poll), Balance /
      // Equity / Leverage / Ccy stayed "—" for the rest of the session. Ask explicitly.
      void this.send("GetAccountRec").catch(() => {
        /* offline again — the next reconnect asks again */
      });
      void this.requestTradeHistory({
        syncSource: "server-reload",
        batchMode: "authoritative",
      });
      this.resyncMarketDataAfterReconnect();
    });
  }

  /**
   * After a reconnect (automatic or manual), resync cached market data so it
   * isn't left stale for the disconnect window. We re-request 1m OHLC for the
   * UNION of every chart slot's selected symbol and every watchlisted/subscribed
   * symbol — the watchlist is the only set whose 1m OHLC we cache, and it was
   * previously skipped (only chart slots were backfilled, so watchlist mini-data
   * kept a gap). Also re-subscribes each symbol so live quotes/candles resume if
   * the server dropped the subscription on the closed connection.
   *
   * The window spans the whole current day (so "today" is fully refreshed, not
   * just the raw gap) plus the disconnect window and 1h padding on each side.
   */
  private resyncMarketDataAfterReconnect(): void {
    const gapStart = this.reconnectingSince;
    this.reconnectingSince = null;

    // Fills that landed while the socket was down are invisible to the blotter otherwise — the
    // open-positions push is not replayed for the gap, so pull the authoritative snapshot.
    this.scheduleOpenTradesResyncFromServer();

    Promise.all([
      import("../../chart/stores/useChartStore3"),
      import("../../subscribed-symbols/stores/useSubscribedSymbolsStore"),
    ])
      .then(([{ useChartStore3 }, { useSubscribedSymbolsStore }]) => {
        const now = new Date();
        const gapMs = gapStart ? Math.max(0, now.getTime() - gapStart) : 0;
        // A long outage (or an unknown one) can have missed a lot; a blink cannot. Anything under
        // 15 minutes gets a gap-sized backfill for the CHARTS only.
        const deepResync = gapStart === null || gapMs > 15 * 60 * 1000;

        // Charts always: they draw candles, so a hole is visible.
        const chartSymbols = new Set<string>();
        for (const slot of Object.values(useChartStore3.getState().slots)) {
          const currency = slot?.selectedSymbol?.currency;
          if (currency) chartSymbols.add(currency);
        }
        // Every subscribed symbol needs its live subscription back, but NOT a bar backfill on a short
        // gap: re-requesting a full day of 1m OHLC for a dozen watchlist rows put megabytes of history
        // in front of the trade commands on the same connection, and the terminal answered a close ~10-15s
        // late while it drained (SR Global, 2026-09-09). Quotes resume from the subscription alone.
        const subscribeOnly = new Set<string>();
        for (const entry of useSubscribedSymbolsStore.getState().allSubscribed) {
          const currency = entry?.symbol?.currency;
          if (currency) subscribeOnly.add(currency);
        }
        const symbols = new Set<string>([...chartSymbols, ...subscribeOnly]);
        if (symbols.size === 0) return;

        const backfill = deepResync ? symbols : chartSymbols;

        const paddingMs = 60 * 60 * 1000;
        const startOfDay = new Date(now);
        startOfDay.setHours(0, 0, 0, 0);
        const from = deepResync
          ? new Date(
              Math.min(startOfDay.getTime(), (gapStart ?? now.getTime()) - paddingMs)
            )
          : new Date((gapStart ?? now.getTime()) - paddingMs);
        const to = new Date(now.getTime() + paddingMs);

        this.logJournal(
          `Reconnect: resubscribing ${symbols.size} symbol(s), OHLC backfill for ${backfill.size} since ${from.toISOString()}` +
            (deepResync ? " (deep)" : " (gap-sized)"),
          "info"
        );

        let i = 0;
        symbols.forEach((symbol) => {
          // Restore the live subscription (idempotent server-side).
          void this.subscribeToSymbol(symbol).catch(() => {
            /* offline again / already subscribed — ignore */
          });
          if (!backfill.has(symbol)) return;
          // Stagger OHLC backfills slightly so a large watchlist doesn't burst
          // the gateway on reconnect.
          const delay = i++ * 40;
          setTimeout(() => {
            void this.send("GetOHLCData", {
              symbol,
              period: 0,
              customFrom: from.toISOString(),
              customTo: to.toISOString(),
            }).catch((error) => {
              console.error(
                `[Chart] Failed to re-request OHLC for ${symbol} after reconnect:`,
                error
              );
            });
          }, delay);
        });
      })
      .catch(() => {
        /* stores unavailable — ignore */
      });
  }

  private setupRawMessageLogging(): void {
    if (!this.connection) return;

    // Override invoke methods to capture outgoing messages
    const originalInvoke = this.connection.invoke.bind(this.connection);
    const originalSend = this.connection.send.bind(this.connection);

    this.connection.invoke = async (methodName: string, ...args: unknown[]) => {
      this.logRawMessage("OUTGOING", methodName, args);
      try {
        return await originalInvoke(methodName, ...args);
      } catch (error) {
        if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
          throw new HubConnectionClosedError(error);
        }
        throw error;
      }
    };

    this.connection.send = async (methodName: string, ...args: unknown[]) => {
      this.logRawMessage("OUTGOING", methodName, args);
      return originalSend(methodName, ...args);
    };

    // Capture ALL incoming messages with universal handler
    this.setupUniversalIncomingHandler();
  }

  private setupUniversalIncomingHandler(): void {
    if (!this.connection) return;

    // Create universal handler for any method
    const universalHandler = (methodName: string) => {
      return (...args: unknown[]) => {
        // Always store ALL messages in raw messages store (including quotes)
        useRawMessagesStore.getState().addMessage("INCOMING", methodName, args);
        journalSignalRIncoming(methodName, args);

        // Handle symbols data
        if (methodName === "VybeOnSymbols" && args.length > 0) {
          this.handleSymbolsData(args[0]);
        }

        // Handle account data
        if (methodName === "VybeOnAccountRec" && args.length > 0) {
          this.handleAccountData(args[0]);
        }

        if (methodName === "VybeOnAccountUpdate" && args.length > 0) {
          this.handleAccountUpdateData(args[0]);
        }

        // Handle opened trades data
        if (methodName === "VybeOnOpenTrades" && args.length > 0) {
          this.handleOpenedTradesData(args[0]);
        }

        // Handle order history data
        if (methodName === "VybeOnOrderHistory" && args.length > 0) {
          this.handleOrderHistoryData(args[0]);
        }

        // Handle order progress events
        if (methodName === "VybeOnOrderProgress" && args.length > 0) {
          this.handleOrderProgressData(args[0]);
          this.showOrderEventToast(methodName, args[0]);
        }

        // Handle order update events
        if (methodName === "VybeOnOrderUpdate" && args.length > 0) {
          this.handleOrderUpdateData(args[0]);
          this.showOrderEventToast(methodName, args[0]);
        }

        // Handle order modify events
        if (methodName === "VybeOnOrderModify" && args.length > 0) {
          this.handleOrderModifyData(args[0]);
        }

        // Handle order send events
        if (methodName === "VybeOnOrderSend" && args.length > 0) {
          this.handleOrderSendData(args[0]);
        }

        if (methodName === "VybeOnOrderClose" && args.length > 0) {
          this.handleOrderCloseData(args[0]);
        }

        // Handle quote history events
        if (methodName === "VybeOnQuoteHistory" && args.length > 0) {
          this.handleQuoteHistoryData(args[0]);
        }

        // Handle connection progress events
        if (methodName === "VybeOnConnectProgress" && args.length > 0) {
          this.handleConnectionProgressData(args[0]);
        }

        // Quote subscription handlers
        if (methodName === "VybeOnQuote" && args.length > 0) {
          this.handleQuoteData(args[0]);
        }

        if (methodName === "VybeOnSubscribe" && args.length > 0) {
          this.handleSubscribeResponse(args[0]);
        }

        if (methodName === "VybeOnUnsubscribe" && args.length > 0) {
          this.handleUnsubscribeResponse(args[0]);
        }

        // Handle symbols update data
        if (methodName === "VybeOnSymbolsUpdate" && args.length > 0) {
          this.handleSymbolsUpdateData(args[0]);
        }

        // Handle OHLC data
        if (methodName === "VybeOnOHLCData" && args.length > 0) {
          this.handleOHLCData(args[0]);
        }

        // Market depth handlers
        if (methodName === "VybeOnMarketDepth" && args.length > 0) {
          this.handleMarketDepthData(args[0]);
        }

        if (methodName === "VybeOnMarketDepthSubscribed" && args.length > 0) {
          this.handleMarketDepthSubscribed(args[0]);
        }

        if (methodName === "VybeOnMarketDepthUnsubscribed" && args.length > 0) {
          this.handleMarketDepthUnsubscribed(args[0]);
        }

        if (methodName === "VybeOnNewMailboxClientMessage" && args.length > 0) {
          this.handleMailboxClientMessage(args[0]);
        }

        if (methodName === "VybeOnNewMailboxServerMessage" && args.length > 0) {
          this.handleMailboxServerMessage(args[0]);
        }

        if (methodName === "VybeOnSyncMailboxBatch" && args.length > 0) {
          this.handleSyncMailboxBatch(args[0]);
        }

        if (methodName === "VybeOnNews" && args.length > 0) {
          this.handleNewsArticle(args[0]);
        }

        if (methodName === "VybeOnSyncNewsBatch" && args.length > 0) {
          this.handleSyncNewsBatch(args[0]);
        }

        if (methodName === "VybeOnEconomicCalendarEvent" && args.length > 0) {
          this.handleEconomicCalendarEvent(args[0]);
        }

        if (methodName === "VybeOnSyncEconomicCalendarBatch" && args.length > 0) {
          this.handleSyncEconomicCalendarBatch(args[0]);
        }

        if (methodName === "VybeOnEconomicCalendarImportComplete" && args.length > 0) {
          this.handleEconomicCalendarImportComplete(args[0]);
        }
      };
    };

    // Register handlers for common SignalR methods
    const commonMethods = [
      "VybeOnConnectProgress",
      "VybeOnSymbols",
      "VybeOnSymbolsUpdate",
      "VybeOnAccountUpdate",
      "VybeOnAccountRec",
      "VybeOnQuote",
      "VybeOnOpenTrades",
      "VybeOnOHLCData",
      "VybeOnOrderHistory",
      "VybeOnOrderProgress",
      "VybeOnOrderUpdate",
      "VybeOnOrderModify",
      "VybeOnOrderSend",
      "VybeOnOrderClose",
      "VybeOnMailInbox",
      "VybeOnNewMailboxClientMessage",
      "VybeOnNewMailboxServerMessage",
      "VybeOnSyncMailboxBatch",
      "VybeOnNews",
      "VybeOnSyncNewsBatch",
      "VybeOnEconomicCalendarEvent",
      "VybeOnSyncEconomicCalendarBatch",
      "VybeOnEconomicCalendarImportComplete",
      "VybeOnConnectionStatus",
      "VybeOnDisconnected",
      "VybeOnSubscribe",
      "VybeOnUnsubscribe",
      "VybeOnQuoteHistory",
      "VybeOnTickHistory",
      "VybeOnMarketDepth",
      "VybeOnMarketDepthSubscribed",
      "VybeOnMarketDepthUnsubscribed",
    ];

    commonMethods.forEach((method) => {
      this.connection!.on(method, universalHandler(method));
    });
  }

  private setConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status;
  }

  private logRawMessage(
    direction: "INCOMING" | "OUTGOING" | "SYSTEM",
    method: string,
    data: unknown
  ): void {
    // Use direct import to avoid circular dependencies
    useRawMessagesStore.getState().addMessage(direction, method, data);
    if (direction === "OUTGOING") {
      const payload = Array.isArray(data) ? data : [data];
      journalSignalROutgoing(method, payload);
    }
  }

  private logJournal(
    content: string,
    type: "info" | "error" | "warning"
  ): void {
    useJournalStore.getState().addEntry({
      level: type,
      category: "connection",
      message: content,
    });
  }

  private showOrderEventToast(methodName: string, data: unknown): void {
    try {
      const payload = Array.isArray(data) ? data[0] : data;
      if (!payload || typeof payload !== "object") return;

      // Read the current notification preferences at emit time so toggling the
      // switches in Settings takes effect immediately without re-subscribing.
      const { notifyOrderUpdate, notifyOrderProgress } =
        useSettingsStore.getState().settings;

      if (methodName === "VybeOnOrderProgress") {
        const progress = payload as Partial<OrderProgress>;
        const status = progress.tradeResult?.status;
        const tradeType = progress.tradeRequest?.tradeType;
        const symbol = progress.tradeRequest?.currency ?? "";
        const requestId = progress.tradeRequest?.requestId;
        const orderTicket = progress.tradeRequest?.orderTicket;
        const positionId = progress.tradeResult?.positionId;

        const parts: string[] = [];
        if (typeof status === "number") {
          parts.push(formatTradeStatus(status));
        }
        if (typeof tradeType === "number") {
          parts.push(formatTradeType(tradeType));
        }
        if (symbol) parts.push(symbol);
        if (typeof requestId === "number" && requestId > 0) {
          parts.push(`req #${requestId}`);
        }
        if (typeof orderTicket === "number" && orderTicket > 0) {
          parts.push(`order #${orderTicket}`);
        }
        if (typeof positionId === "number" && positionId > 0) {
          parts.push(`pos #${positionId}`);
        }

        const description = parts.join(" • ") || "Order progress";
        const isError =
          typeof status === "number" && this.isProgressErrorStatus(status);

        if (isError) {
          // Send / close / modify verdicts are toasted from their dedicated
          // response handlers — skip them here to avoid double-toasting the
          // same failed request.
          if (
            typeof tradeType === "number" &&
            this.isResponseToastedTradeType(tradeType)
          ) {
            return;
          }
          // Errors always surface, regardless of the progress-notification
          // setting — and with a human title instead of the raw method name.
          toast.error("Order error", { description });
        } else {
          if (!notifyOrderProgress) return;
          toast.info("VybeOnOrderProgress", { description });
        }
        return;
      }

      if (methodName === "VybeOnOrderUpdate") {
        if (!notifyOrderUpdate) return;
        const update = payload as Partial<OrderUpdate>;
        const updateType = update.type;
        const order = update.order;
        const deal = update.deal;
        const symbol = order?.symbol ?? deal?.symbol ?? "";
        const ticket = order?.ticket ?? deal?.ticketNumber;
        const lots = order?.closeLots ?? order?.lots ?? deal?.lots;
        const profit = order?.profit ?? deal?.profit;
        const closePrice = order?.closePrice;
        const openPrice = order?.openPrice;

        const parts: string[] = [];
        if (typeof updateType === "number") {
          parts.push(formatUpdateType(updateType));
        }
        if (symbol) parts.push(symbol);
        if (typeof ticket === "number" && ticket > 0) {
          parts.push(`#${ticket}`);
        }
        if (typeof lots === "number" && lots > 0) {
          parts.push(`${lots} lots`);
        }
        if (typeof openPrice === "number" && openPrice > 0) {
          parts.push(`open ${openPrice}`);
        }
        if (typeof closePrice === "number" && closePrice > 0) {
          parts.push(`close ${closePrice}`);
        }
        if (typeof profit === "number" && profit !== 0) {
          parts.push(`P/L ${profit}`);
        }

        const description = parts.join(" • ") || "Order update";
        toast.info("VybeOnOrderUpdate", { description });
        return;
      }
    } catch (error) {
      console.error("[OrderToast] Failed to render toast:", error);
    }
  }

  /**
   * Trade types whose failure verdict is already toasted by the dedicated
   * response handlers (handleOrderSendData / handleOrderCloseData /
   * handleOrderModifyData) — progress errors for these are skipped so a single
   * failed request never toasts twice.
   */
  private isResponseToastedTradeType(tradeType: number): boolean {
    switch (tradeType) {
      case VybeTradeType.TradePrice:
      case VybeTradeType.RequestExecution:
      case VybeTradeType.InstantExecution:
      case VybeTradeType.MarketExecution:
      case VybeTradeType.ExchangeExecution:
      case VybeTradeType.ModifyDeal:
      case VybeTradeType.ModifyOrder:
      case VybeTradeType.ClosePosition:
        return true;
      default:
        return false;
    }
  }

  private isProgressErrorStatus(status: number): boolean {
    switch (status) {
      case VybeMsg.COMMON_ERROR:
      case VybeMsg.INVALID_PARAM:
      case VybeMsg.INVALID_DATA:
      case VybeMsg.NETWORK_ERROR:
      case VybeMsg.NOT_PERMISSION:
      case VybeMsg.OPERATION_TIMEOUT:
      case VybeMsg.NO_CONNECTION:
      case VybeMsg.SERVICE_NOT_AVAILABLE:
      case VybeMsg.REQUEST_REJECTED:
      case VybeMsg.REQUEST_CANCELLED:
      case VybeMsg.REQUEST_ERROR:
      case VybeMsg.REQUEST_TIMEOUT:
      case VybeMsg.INVALID_REQUEST:
      case VybeMsg.INVALID_VOLUME:
      case VybeMsg.INVALID_PRICE:
      case VybeMsg.INVALID_STOPS:
      case VybeMsg.TRADE_DISABLED:
      case VybeMsg.MARKET_CLOSED:
      case VybeMsg.NO_MONEY:
      case VybeMsg.INVALID_EXPIRATION:
      case VybeMsg.MODIFICATION_FAILED:
      case VybeMsg.UNSUPPORTED_FILLING_MODE:
      case VybeMsg.ORDER_CANCELLED:
      case VybeMsg.FIFO_CLOSE:
      case VybeMsg.HEDGE_PROHIBITED:
        return true;
      default:
        return false;
    }
  }

  private handleSymbolsData(data: unknown): void {
    try {
      // Null-guard: server error paths may send responses with null symbols/groups.
      // Normalize them to empty arrays so the type guards and batch processing
      // below never iterate null.
      if (data !== null && typeof data === "object" && !Array.isArray(data)) {
        const record = data as Record<string, unknown>;
        if (record.symbols == null) record.symbols = [];
        if (record.groups == null) record.groups = [];
      }
      // Import symbols store dynamically to avoid circular dependencies
      import("../../symbols/stores/useSymbolsStore").then(
        ({ useSymbolsStore }) => {
          // Check if this is a simple symbols response (no batch info)
          if (this.isSimpleSymbolsResponse(data)) {
            // Convert to batch format for processing
            const simpleBatch: any = {
              batchNumber: 0,
              totalBatches: 1,
              totalSymbolsCount: (data as any).symbols.length,
              isFinalBatch: true,
              success: (data as any).success ?? true,
              errorMessage: (data as any).errorMessage || null,
              symbols: (data as any).symbols,
              groups: (data as any).groups || [],
              baseInfo: (data as any).baseInfo || null,
            };

            useSymbolsStore.getState().processBatch(simpleBatch);
            return;
          }

          // Otherwise handle as normal batch processing
          const batches = Array.isArray(data) ? data : [data];

          batches.forEach((batch: unknown, index: number) => {
            if (this.isSymbolsBatch(batch)) {
              // Runtime data from SignalR - trust the data structure matches our interface
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              useSymbolsStore.getState().processBatch(batch as any);
            } else {
              console.warn(`[VybeOnSymbols] Batch ${index} failed validation`);
            }
          });
        }
      );
    } catch (error) {
      console.error("[Symbols] Failed to process symbols data:", error);
    }
  }

  private isSymbolsBatch(
    data: unknown
  ): data is { batchNumber: number; totalBatches: number; symbols: unknown[] } {
    return (
      data !== null &&
      typeof data === "object" &&
      "symbols" in data &&
      "batchNumber" in data &&
      "totalBatches" in data &&
      Array.isArray((data as { symbols: unknown }).symbols)
    );
  }

  private isSimpleSymbolsResponse(data: unknown): data is {
    success: boolean;
    symbols: unknown[];
    errorMessage?: string | null;
    groups?: unknown[];
    baseInfo?: unknown;
  } {
    return (
      data !== null &&
      typeof data === "object" &&
      "symbols" in data &&
      "success" in data &&
      !("batchNumber" in data) && // Key difference - no batch info
      !("totalBatches" in data) &&
      Array.isArray((data as { symbols: unknown }).symbols)
    );
  }

  private handleAccountData(data: unknown): void {
    try {
      // Import account store dynamically to avoid circular dependencies
      import(
        "../../account-information/stores/useAccountInformationStore"
      ).then(({ useAccountInformationStore }) => {
        if (this.isAccountData(data)) {
          // Runtime data from SignalR - trust the data structure matches our interface
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          useAccountInformationStore.getState().processAccountData(data as any);
        }
      });
    } catch (error) {
      console.error(
        "[AccountInformation] Failed to process account data:",
        error
      );
    }
  }

  private isAccountData(data: unknown): boolean {
    return (
      data !== null &&
      typeof data === "object" &&
      "success" in data &&
      "account" in data
    );
  }

  private handleAccountUpdateData(data: unknown): void {
    try {
      import(
        "../../account-information/stores/useAccountInformationStore"
      ).then(({ useAccountInformationStore }) => {
        if (this.isAccountUpdateData(data)) {
          useAccountInformationStore
            .getState()
            .processAccountUpdate(data as any);
        }
      });
    } catch (error) {
      console.error(
        "[AccountInformation] Failed to process account update:",
        error
      );
    }
  }

  private isAccountUpdateData(data: unknown): boolean {
    return (
      data !== null &&
      typeof data === "object" &&
      "login" in data &&
      "equity" in data &&
      "margin" in data &&
      "freeMargin" in data
    );
  }

  private handleOpenedTradesData(data: unknown): void {
    try {
      // Import opened trades store dynamically to avoid circular dependencies
      import("../../opened-trades/stores/useOpenedTradesStore").then(
        ({ useOpenedTradesStore }) => {
          if (this.isOpenedTradesData(data)) {
            // Runtime data from SignalR - trust the data structure matches our interface
            useOpenedTradesStore
              .getState()
              .processOpenedTrades(data as OpenedTradesResponse);
            const orders = (data as OpenedTradesResponse).orders;

            // Debug: Log first trade to verify new schema
            if (orders.length > 0) {
            }
          } else {
            console.warn(
              "[OpenedTrades] Invalid opened trades data format:",
              data
            );
          }
        }
      );
    } catch (error) {
      console.error(
        "[OpenedTrades] Failed to process opened trades data:",
        error
      );
    }
  }

  private isOpenedTradesData(data: unknown): boolean {
    if (
      data === null ||
      typeof data !== "object" ||
      !("success" in data) ||
      !("orders" in data) ||
      !("totalCount" in data) ||
      !Array.isArray((data as { orders: unknown }).orders)
    ) {
      return false;
    }

    // Validate first trade structure if exists
    const orders = (data as { orders: unknown[] }).orders;
    if (orders.length > 0) {
      return this.isValidOpenedTrade(orders[0]);
    }

    return true;
  }

  private isValidOpenedTrade(trade: unknown): boolean {
    if (
      trade === null ||
      typeof trade !== "object" ||
      !("ticket" in trade) ||
      !("symbol" in trade) ||
      !("orderType" in trade) ||
      !("dealType" in trade)
    ) {
      return false;
    }

    const tradeObj = trade as Record<string, unknown>;
    return (
      typeof tradeObj.ticket === "number" &&
      typeof tradeObj.orderType === "number" && // Now expecting numeric
      typeof tradeObj.dealType === "number" // Now expecting numeric
    );
  }

  private handleOrderProgressData(data: unknown): void {
    try {
      // Import opened trades store dynamically to avoid circular dependencies
      import("../../opened-trades/stores/useOpenedTradesStore").then(
        ({ useOpenedTradesStore }) => {
          if (this.isOrderProgressData(data)) {
            // Runtime data from SignalR - trust the data structure matches our interface
            useOpenedTradesStore
              .getState()
              .processOrderProgress(data as OrderProgress);
          } else {
            console.warn(
              "[OrderProgress] Invalid order progress data format:",
              data
            );
          }
        }
      );
    } catch (error) {
      console.error(
        "[OrderProgress] Failed to process order progress data:",
        error
      );
    }
  }

  private isOrderProgressData(data: unknown): boolean {
    if (
      data === null ||
      typeof data !== "object" ||
      !("orderUpdate" in data) ||
      !("tradeRequest" in data) ||
      !("tradeResult" in data)
    ) {
      return false;
    }

    const dataObj = data as Record<string, unknown>;
    return (
      typeof dataObj.orderUpdate === "object" &&
      typeof dataObj.tradeRequest === "object" &&
      typeof dataObj.tradeResult === "object"
    );
  }

  private handleOrderUpdateData(data: unknown): void {
    try {
      // Import opened trades store dynamically to avoid circular dependencies
      import("../../opened-trades/stores/useOpenedTradesStore").then(
        ({ useOpenedTradesStore }) => {
          if (this.isOrderUpdateData(data)) {
            // Runtime data from SignalR - trust the data structure matches our interface
            useOpenedTradesStore
              .getState()
              .processOrderUpdate(data as OrderUpdate);
            import("../../trade-history/stores/useTradeHistoryStore").then(
              ({ useTradeHistoryStore }) => {
                useTradeHistoryStore
                  .getState()
                  .applyRealtimeOrderUpdate(data as OrderUpdate);
              }
            );
            this.scheduleHistoryResyncFromServer();
            this.scheduleOpenTradesResyncFromServer();
          } else {
            console.warn(
              "[OrderUpdate] Invalid order update data format:",
              data
            );
          }
        }
      );
    } catch (error) {
      console.error("[OrderUpdate] Failed to process order update:", error);
    }
  }

  private handleMailboxClientMessage(data: unknown): void {
    import("../../mailbox/stores/useMailboxStore").then(({ useMailboxStore, mapWireMailboxMessage }) => {
      const raw = (Array.isArray(data) ? data[0] : data) as Record<string, unknown>;
      if (!raw || typeof raw !== "object") return;
      useMailboxStore.getState().addMessage(mapWireMailboxMessage(raw));
    });
  }

  private handleMailboxServerMessage(data: unknown): void {
    this.handleMailboxClientMessage(data);
  }

  private handleSyncMailboxBatch(data: unknown): void {
    import("../../mailbox/stores/useMailboxStore").then(({ useMailboxStore, mapWireMailboxMessage }) => {
      const raw = (Array.isArray(data) ? data[0] : data) as Record<string, unknown>;
      if (!raw || typeof raw !== "object") return;

      const accountKey = String(raw.accountKey ?? raw.AccountKey ?? "");
      const isLastBatch = Boolean(raw.isLastBatch ?? raw.IsLastBatch ?? false);
      const messagesRaw = (raw.messages ?? raw.Messages) as unknown;
      const batchMessages = Array.isArray(messagesRaw)
        ? messagesRaw.map((m) => mapWireMailboxMessage(m as Record<string, unknown>))
        : [];

      useMailboxStore.getState().applySyncBatch(accountKey, batchMessages, isLastBatch);
    });
  }

  public async sendMailboxMessage(payload: {
    to: string;
    subject: string;
    body: string;
    severity?: number;
    topicId?: string;
  }): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("SendMailboxMessage", payload);
  }

  public async markMailboxMessageRead(messageId: string): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("MarkMailboxMessageRead", messageId);
  }

  public async syncMailbox(request: {
    isFirstLoad: boolean;
    lastMsgSequence: number;
    missingMsgSequences: number[];
  }): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("SyncMailbox", {
      isFirstLoad: request.isFirstLoad,
      lastMsgSequence: request.lastMsgSequence,
      missingMsgSequences: request.missingMsgSequences,
    });
  }

  private handleNewsArticle(data: unknown): void {
    import("../../news/stores/useNewsStore").then(
      async ({ useNewsStore, mapWireNewsArticle }) => {
        const raw = (Array.isArray(data) ? data[0] : data) as Record<string, unknown>;
        if (!raw || typeof raw !== "object") return;

        const article = mapWireNewsArticle(raw);
        const store = useNewsStore.getState();
        const existingArticles = store.articles;

        const { shouldNotifyNewsArticle, notifyNewsArticle } = await import(
          "../../news/newsNotifications"
        );
        if (shouldNotifyNewsArticle(article, existingArticles)) {
          notifyNewsArticle(article);
        }

        store.upsertArticle(article);
      }
    );
  }

  private handleSyncNewsBatch(data: unknown): void {
    import("../../news/stores/useNewsStore").then(({ useNewsStore, mapWireNewsArticle }) => {
      const raw = (Array.isArray(data) ? data[0] : data) as Record<string, unknown>;
      if (!raw || typeof raw !== "object") return;

      const isLastBatch = Boolean(raw.isLastBatch ?? raw.IsLastBatch ?? false);
      const articlesRaw = (raw.articles ?? raw.Articles) as unknown;
      const batchArticles = Array.isArray(articlesRaw)
        ? articlesRaw.map((a) => mapWireNewsArticle(a as Record<string, unknown>))
        : [];

      useNewsStore.getState().applySyncBatch(batchArticles, isLastBatch);
    });
  }

  public async syncNews(request: {
    isFirstLoad: boolean;
    lastSequence: number;
    missingSequences: number[];
  }): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("SyncNews", {
      isFirstLoad: request.isFirstLoad,
      lastSequence: request.lastSequence,
      missingSequences: request.missingSequences,
    });
  }

  private handleEconomicCalendarEvent(data: unknown): void {
    import("../../economic-calendar/stores/useEconomicCalendarStore").then(
      ({ useEconomicCalendarStore, mapWireEconomicEvent }) => {
        const raw = (Array.isArray(data) ? data[0] : data) as Record<
          string,
          unknown
        >;
        if (!raw || typeof raw !== "object") return;
        useEconomicCalendarStore.getState().applyLiveEvent(mapWireEconomicEvent(raw));
      }
    );
  }

  private handleSyncEconomicCalendarBatch(data: unknown): void {
    import("../../economic-calendar/stores/useEconomicCalendarStore").then(
      ({ useEconomicCalendarStore, mapWireEconomicEvent }) => {
        const raw = (Array.isArray(data) ? data[0] : data) as Record<
          string,
          unknown
        >;
        if (!raw || typeof raw !== "object") return;

        const isLastBatch = Boolean(raw.isLastBatch ?? raw.IsLastBatch ?? false);
        const batchIndex = Number(raw.batchIndex ?? raw.BatchIndex ?? 0);
        const replaceScope = String(
          raw.replaceScope ?? raw.ReplaceScope ?? ""
        ).trim() || null;
        const scopeKey = String(raw.scopeKey ?? raw.ScopeKey ?? "").trim() || null;
        const eventsRaw = (raw.events ?? raw.Events) as unknown;
        const batch = Array.isArray(eventsRaw)
          ? eventsRaw.map((e) =>
              mapWireEconomicEvent(e as Record<string, unknown>)
            )
          : [];

        const monthVersionsRaw = (raw.monthVersions ?? raw.MonthVersions) as unknown;
        const serverMonthVersions = Array.isArray(monthVersionsRaw)
          ? monthVersionsRaw.map((v) => {
              const row = v as Record<string, unknown>;
              return {
                monthKey: String(row.monthKey ?? row.MonthKey ?? ""),
                versionHash: String(row.versionHash ?? row.VersionHash ?? ""),
              };
            })
          : [];

        useEconomicCalendarStore
          .getState()
          .applySyncBatch(
            batch,
            isLastBatch,
            replaceScope,
            scopeKey,
            batchIndex,
            serverMonthVersions
          );
      }
    );
  }

  public async syncEconomicCalendar(request: {
    calendarYears: number[];
    monthVersions: Array<{ monthKey: string; versionHash: string }>;
    isFirstLoad?: boolean;
    lastSequence?: number;
    missingSequences?: number[];
  }): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("SyncEconomicCalendar", {
      calendarYears: request.calendarYears,
      monthVersions: request.monthVersions,
    });
  }

  public async syncEconomicCalendarDay(request: {
    dayKey: string;
  }): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("SyncEconomicCalendarDay", {
      dayKey: request.dayKey,
    });
  }

  public async syncEconomicCalendarMonth(request: {
    monthKey: string;
  }): Promise<void> {
    if (!this.connection) throw new Error("Not connected");
    await this.connection.invoke("SyncEconomicCalendarMonth", {
      monthKey: request.monthKey,
    });
  }

  private trySyncEconomicCalendarSession(): void {
    import("./VybeTraderClient")
      .then(({ VybeTraderClient }) =>
        import("../../economic-calendar/syncEconomicCalendar").then(
          ({ syncEconomicCalendarOnSessionConnect }) => {
            const client = VybeTraderClient.getInstance();
            if (client.getConnectionStatus() === "connected") {
              void syncEconomicCalendarOnSessionConnect(client);
            }
          }
        )
      )
      .catch(() => {
        /* ignore if modules unavailable */
      });
  }

  private trySyncNewsSession(): void {
    import("./VybeTraderClient")
      .then(({ VybeTraderClient }) =>
        import("../../news/syncNews").then(({ prepareAndSyncNews }) => {
          const client = VybeTraderClient.getInstance();
          if (client.getConnectionStatus() === "connected") {
            void prepareAndSyncNews(client);
          }
        })
      )
      .catch(() => {
        /* ignore if modules unavailable */
      });
  }

  private lastImportCompleteSyncAt = 0;

  private handleEconomicCalendarImportComplete(_data: unknown): void {
    // Debounce: a server-side import-complete broadcast triggers a full multi-year re-sync (34k events,
    // index rebuild). Coalesce bursts so the terminal can't be pushed into a re-render loop.
    const now = Date.now();
    if (now - this.lastImportCompleteSyncAt < 30_000) return;
    this.lastImportCompleteSyncAt = now;

    import("./VybeTraderClient")
      .then(({ VybeTraderClient }) =>
        import("../../economic-calendar/syncEconomicCalendar").then(
          ({ prepareAndSyncEconomicCalendar }) => {
            const client = VybeTraderClient.getInstance();
            if (client.getConnectionStatus() === "connected") {
              void prepareAndSyncEconomicCalendar(client);
            }
          }
        )
      )
      .catch(() => {
        /* ignore if modules unavailable */
      });
  }

  private handleOrderModifyData(data: unknown): void {
    try {
      if (this.isOrderModifyData(data)) {
        useJournalStore.getState().addEntry({
          level:
            data &&
            typeof data === "object" &&
            "success" in data &&
            (data as { success: boolean }).success === false
              ? "warning"
              : "info",
          category: "trade",
          message: "OrderModify response",
          detail: data,
        });
        // Import dedicated order modify store
        import("../../order-modify/stores/useOrderModifyStore").then(
          ({ useOrderModifyStore }) => {
            // Add the event to store for components to react to
            useOrderModifyStore.getState().addOrderModifyEvent(data);
          }
        );
        if (
          data &&
          typeof data === "object" &&
          "success" in data &&
          (data as { success: boolean }).success === true
        ) {
          playNotificationSound("orderSuccess");
          import("../../opened-trades/stores/useOpenedTradesStore").then(
            ({ useOpenedTradesStore }) => {
              useOpenedTradesStore
                .getState()
                .processOrderModify(data as OrderModifyResponse);
            }
          );
          this.scheduleHistoryResyncFromServer();
          this.scheduleOpenTradesResyncFromServer();
        } else if (
          data &&
          typeof data === "object" &&
          "success" in data &&
          (data as { success: boolean }).success === false
        ) {
          toast.error("Update failed", {
            description:
              this.extractErrorMessage(data) ??
              "Trade level update was rejected",
          });
        }
      } else {
        console.warn("[OrderModify] Invalid order modify data format:", data);
      }
    } catch (error) {
      console.error("[OrderModify] Failed to process order modify:", error);
    }
  }

  private handleOrderSendData(data: unknown): void {
    try {
      console.log("[OrderSend] Order send event received:", data);
      useJournalStore.getState().addEntry({
        level:
          data &&
          typeof data === "object" &&
          "success" in data &&
          (data as { success: boolean }).success === false
            ? "warning"
            : "info",
        category: "trade",
        message: "OrderSend response",
        detail: data,
      });
      if (
        data &&
        typeof data === "object" &&
        "success" in data &&
        (data as { success: boolean }).success === true
      ) {
        playNotificationSound("orderSuccess");
        this.scheduleHistoryResyncFromServer();
        this.scheduleOpenTradesResyncFromServer();
      } else if (
        data &&
        typeof data === "object" &&
        "success" in data &&
        (data as { success: boolean }).success === false
      ) {
        toast.error("Order failed", {
          description: this.extractErrorMessage(data) ?? "Order was rejected",
        });
        // Resync so the blotter self-heals if the client's view diverged.
        this.scheduleOpenTradesResyncFromServer();
      }
    } catch (error) {
      console.error("[OrderSend] Failed to process order send:", error);
    }
  }

  private handleOrderCloseData(data: unknown): void {
    try {
      useJournalStore.getState().addEntry({
        level:
          data &&
          typeof data === "object" &&
          "success" in data &&
          (data as { success: boolean }).success === false
            ? "warning"
            : "info",
        category: "trade",
        message: "OrderClose response",
        detail: data,
      });
      if (
        data &&
        typeof data === "object" &&
        "success" in data &&
        (data as { success: boolean }).success === true
      ) {
        playNotificationSound("orderSuccess");
        this.scheduleHistoryResyncFromServer();
        this.scheduleOpenTradesResyncFromServer();
      } else if (
        data &&
        typeof data === "object" &&
        "success" in data &&
        (data as { success: boolean }).success === false
      ) {
        toast.error("Close failed", {
          description:
            this.extractErrorMessage(data) ?? "Close request was rejected",
        });
        // A failed close (especially "position not found") means the client's
        // open-trades view has diverged from the server — pull the authoritative
        // snapshot so phantom rows self-heal.
        this.scheduleOpenTradesResyncFromServer();
      }
    } catch (error) {
      console.error("[OrderClose] Failed to process order close event:", error);
    }
  }

  /** Best-effort human error message from an order response payload. */
  private extractErrorMessage(data: unknown): string | null {
    if (!data || typeof data !== "object") return null;
    const record = data as Record<string, unknown>;
    const message = record.errorMessage ?? record.ErrorMessage ?? record.message;
    return typeof message === "string" && message.length > 0 ? message : null;
  }

  private handleQuoteHistoryData(data: unknown): void {
    try {
      // Log the quote history event for debugging as requested
      console.log("[QuoteHistory] Quote history event received:", data);
      
      // For now, just log - no processing needed
      
    } catch (error) {
      console.error("[QuoteHistory] Failed to process quote history:", error);
    }
  }

  private isOrderUpdateData(data: unknown): boolean {
    if (
      data === null ||
      typeof data !== "object" ||
      !("trans" in data) ||
      !("deal" in data) ||
      !("oppositeDeal" in data) ||
      !("order" in data) ||
      !("type" in data)
    ) {
      return false;
    }

    const dataObj = data as Record<string, unknown>;
    return (
      (dataObj.order === null || typeof dataObj.order === "object") &&
      typeof dataObj.type === "number"
    );
  }

  private isOrderModifyData(data: unknown): boolean {
    // Simple validation for order modify - just check if it has success field
    if (
      data === null ||
      typeof data !== "object" ||
      !("success" in data)
    ) {
      return false;
    }

    const dataObj = data as Record<string, unknown>;
    return typeof dataObj.success === "boolean";
  }

  // Quote and subscription handlers
  private handleQuoteData(data: unknown): void {
    try {
      this.updateQuoteStore(data);
    } catch (error) {
      console.error("[Quotes] Failed to process quote data:", error);
    }
  }

  private handleConnectionProgressData(data: unknown): void {
    try {
      // Import connection progress store dynamically to avoid circular dependencies
      import(
        "../../connection-progress/stores/useConnectionProgressStore"
      ).then(({ useConnectionProgressStore }) => {
        // SignalR sends data as array, extract first element
        const progressData = Array.isArray(data) ? data[0] : data;

        if (this.isConnectionProgressData(progressData)) {
          // Runtime data from SignalR - trust the data structure matches our interface
          const typedProgressData = progressData as ConnectionProgressBatch;
          useConnectionProgressStore
            .getState()
            .processMessage(typedProgressData);

          // VybeProgressType: 0=Info, 1=Warning, 2=Error, 3=Success
          useJournalStore.getState().addEntry({
            level:
              typedProgressData.type === 2
                ? "error"
                : typedProgressData.type === 1
                  ? "warning"
                  : "info",
            category: "platform",
            message: `Connect progress ${typedProgressData.progress}%: ${typedProgressData.message}`,
            detail: {
              progress: typedProgressData.progress,
              type: typedProgressData.type,
            },
            sanitizeDetail: false,
          });

          // A VybeProgressType.Error frame (e.g. rejected login) latches an
          // error state that reconnect attempts must not paper over, and arms
          // the auto-reconnect retry cap. A success frame clears the latch.
          if (typedProgressData.type === 2) {
            this.loginRejected = true;
          } else if (
            typedProgressData.type === 3 &&
            typedProgressData.progress === 100
          ) {
            this.loginRejected = false;
          }

          // Auto-request trade history when connection is complete (progress 100% and type 3)
          if (
            typedProgressData.progress === 100 &&
            typedProgressData.type === 3
          ) {
            this.requestTradeHistory();
          }
        } else {
          console.warn(
            "[ConnectionProgress] Invalid progress data format:",
            progressData
          );
        }
      });
    } catch (error) {
      console.error(
        "[ConnectionProgress] Failed to process connection progress data:",
        error
      );
    }
  }

  private handleSubscribeResponse(data: unknown): void {
    try {
      console.log("[Subscribe] Subscribe response received:", data);
      useJournalStore.getState().addEntry({
        level: "info",
        category: "quotes",
        message: "Subscribe response",
        detail: data,
      });

      import("../../subscribed-symbols/stores/useSubscribedSymbolsStore").then(
        ({ useSubscribedSymbolsStore }) => {
          if (this.isSubscriptionResponse(data)) {
            // Runtime data from SignalR - trust the data structure matches our interface
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = data as any;
            if (response.success) {
              useSubscribedSymbolsStore
                .getState()
                .setSignalRSubscriptionStatus(response.symbol, true);
            } else {
              console.error(
                `[Quotes] Failed to subscribe to ${response.symbol}: ${response.errorMessage}`
              );
            }
          }
        }
      );
    } catch (error) {
      console.error("[Quotes] Failed to process subscribe response:", error);
    }
  }

  private handleUnsubscribeResponse(data: unknown): void {
    try {
      useJournalStore.getState().addEntry({
        level: "info",
        category: "quotes",
        message: "Unsubscribe response",
        detail: data,
      });

      import("../../subscribed-symbols/stores/useSubscribedSymbolsStore").then(
        ({ useSubscribedSymbolsStore }) => {
          if (this.isSubscriptionResponse(data)) {
            // Runtime data from SignalR - trust the data structure matches our interface
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = data as any;
            if (response.success) {
              useSubscribedSymbolsStore
                .getState()
                .setSignalRSubscriptionStatus(response.symbol, false);
            } else {
              console.error(
                `[Quotes] Failed to unsubscribe from ${response.symbol}: ${response.errorMessage}`
              );
            }
          }
        }
      );
    } catch (error) {
      console.error("[Quotes] Failed to process unsubscribe response:", error);
    }
  }

  private handleOHLCData(data: unknown): void {
    try {
      import("../../chart/stores/useChartStore3").then(({ useChartStore3 }) => {
        // The data can come either as an array or as a direct object
        let batchData = data;

        // If it's an array, take the first element
        if (Array.isArray(data) && data.length > 0) {
          batchData = data[0];
        }

        // Process the batch data directly
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        useChartStore3.getState().processBatch(batchData as any);
      });
    } catch (error) {
      console.error("[Chart] Failed to process OHLC data:", error);
    }
  }

  private isQuoteData(data: unknown): boolean {
    return (
      data !== null &&
      typeof data === "object" &&
      "symbol" in data &&
      "bid" in data &&
      "ask" in data &&
      "time" in data
    );
  }

  private isSubscriptionResponse(data: unknown): boolean {
    return (
      data !== null &&
      typeof data === "object" &&
      "success" in data &&
      "symbol" in data
    );
  }

  private isConnectionProgressData(data: unknown): boolean {
    return (
      data !== null &&
      typeof data === "object" &&
      "message" in data &&
      "progress" in data &&
      "type" in data &&
      typeof (data as { progress: unknown }).progress === "number" &&
      typeof (data as { type: unknown }).type === "number"
    );
  }

  private handleOrderHistoryData(data: unknown): void {
    try {
      // Import trade history store dynamically to avoid circular dependencies
      import("../../trade-history/stores/useTradeHistoryStore").then(
        ({ useTradeHistoryStore }) => {
          // SignalR sends data as array, extract first element like connection progress
          const historyData = Array.isArray(data) ? data[0] : data;

          if (this.isOrderHistoryData(historyData)) {
            const raw = historyData as Record<string, unknown>;
            const orders = Array.isArray(raw.orders)
              ? raw.orders
              : raw.orders == null
                ? []
                : [];
            const internals = Array.isArray(raw.internals)
              ? raw.internals
              : raw.internals == null
                ? []
                : [];
            const deals = Array.isArray(raw.deals)
              ? raw.deals
              : raw.deals == null
                ? []
                : [];
            const totalCount =
              typeof raw.totalCount === "number" ? raw.totalCount : 0;
            const historySource =
              typeof raw.historySource === "string"
                ? raw.historySource
                : undefined;

            const success =
              typeof raw.success === "boolean" ? raw.success : false;
            const meta = this.popHistoryResponseMeta();
            const extendedBatch: TradeHistoryBatch = {
              success,
              errorMessage:
                typeof raw.errorMessage === "string"
                  ? raw.errorMessage
                  : !success
                    ? "Order history error"
                    : null,
              orders: orders as TradeHistoryBatch["orders"],
              internals: internals as TradeHistoryBatch["internals"],
              deals: deals as TradeHistoryBatch["deals"],
              totalCount,
              historySource,
              syncSource: meta.syncSource,
              batchMode: meta.batchMode,
            };

            useTradeHistoryStore.getState().processBatch(extendedBatch);
          } else {
            console.warn(
              "[TradeHistory] Invalid order history data format:",
              historyData
            );
          }
        }
      );
    } catch (error) {
      console.error(
        "[TradeHistory] Failed to process order history data:",
        error
      );
    }
  }

  private isOrderHistoryData(data: unknown): boolean {
    if (data === null || typeof data !== "object" || !("success" in data)) {
      return false;
    }
    return typeof (data as { success: unknown }).success === "boolean";
  }

  private isValidTradeOrder(order: unknown): boolean {
    if (
      order === null ||
      typeof order !== "object" ||
      !("ticket" in order) ||
      !("symbol" in order) ||
      !("orderType" in order) ||
      !("dealType" in order)
    ) {
      return false;
    }

    const orderObj = order as Record<string, unknown>;
    return (
      typeof orderObj.ticket === "number" &&
      typeof orderObj.orderType === "number" && // Now expecting numeric
      typeof orderObj.dealType === "number" // Now expecting numeric
    );
  }

  // Quote subscription methods
  public async subscribeToSymbol(currency: string): Promise<void> {
    if (!this.connection) {
      throw new Error("Not connected to SignalR");
    }

    try {
      await this.connection.invoke("Subscribe", { symbol: currency });
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        return;
      }
      console.error(`[SignalR] Failed to subscribe to ${currency}:`, error);
      throw error;
    }
  }

  public async unsubscribeFromSymbol(currency: string): Promise<void> {
    if (!this.connection) {
      throw new Error("Not connected to SignalR");
    }

    try {
      await this.connection.invoke("Unsubscribe", { symbol: currency });
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        return;
      }
      console.error(`[SignalR] Failed to unsubscribe from ${currency}:`, error);
      throw error;
    }
  }

  // Market depth subscription methods
  public async subscribeMarketDepth(symbol: string): Promise<void> {
    if (!this.connection) {
      throw new Error("Not connected to SignalR");
    }

    try {
      // Optimistically mark as subscribing in the store so the panel can show a loading state.
      const { useMarketDepthStore } = await import(
        "../../order-book/stores/useMarketDepthStore"
      );
      useMarketDepthStore.getState().setSubscribing(symbol);

      await this.connection.invoke("SubscribeMarketDepth", { symbol });
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        return;
      }
      console.error(`[SignalR] Failed to subscribe market depth for ${symbol}:`, error);
      throw error;
    }
  }

  public async unsubscribeMarketDepth(symbol: string): Promise<void> {
    if (!this.connection) {
      throw new Error("Not connected to SignalR");
    }

    try {
      await this.connection.invoke("UnsubscribeMarketDepth", { symbol });
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        return;
      }
      console.error(`[SignalR] Failed to unsubscribe market depth for ${symbol}:`, error);
      throw error;
    }
  }

  private handleMarketDepthData(data: unknown): void {
    try {
      if (!this.isSymbolBookData(data)) return;
      const book = data as {
        symbol: string;
        time?: number;
        updateMask?: number;
        bars: Array<{
          updateMask?: number;
          type: number;
          price: number;
          volume: number;
          lots: number;
          s19?: number;
        }>;
        operation?: number;
      };
      import("../../order-book/stores/useMarketDepthStore").then(
        ({ useMarketDepthStore }) => {
          useMarketDepthStore.getState().applyMarketDepth({
            symbol: book.symbol,
            time: book.time ?? 0,
            updateMask: book.updateMask ?? 0,
            bars: (book.bars ?? []).map((bar) => ({
              updateMask: bar.updateMask ?? 0,
              type: bar.type,
              price: bar.price,
              volume: bar.volume,
              lots: bar.lots,
              s19: bar.s19,
            })),
            operation: book.operation ?? 0,
          });
        }
      );
    } catch (error) {
      console.error("[MarketDepth] Failed to process market depth:", error);
    }
  }

  private handleMarketDepthSubscribed(data: unknown): void {
    try {
      if (!data || typeof data !== "object") return;
      const response = data as {
        symbol?: string;
        success?: boolean;
        errorMessage?: string | null;
        depthOfMarket?: number;
      };
      if (typeof response.symbol !== "string") return;
      import("../../order-book/stores/useMarketDepthStore").then(
        ({ useMarketDepthStore }) => {
          useMarketDepthStore.getState().applySubscribeAck({
            symbol: response.symbol!,
            success: !!response.success,
            errorMessage: response.errorMessage ?? null,
            depthOfMarket: response.depthOfMarket ?? 0,
          });
        }
      );
    } catch (error) {
      console.error("[MarketDepth] Failed to process subscribe ack:", error);
    }
  }

  private handleMarketDepthUnsubscribed(data: unknown): void {
    try {
      if (!data || typeof data !== "object") return;
      const response = data as { symbol?: string };
      if (typeof response.symbol !== "string") return;
      import("../../order-book/stores/useMarketDepthStore").then(
        ({ useMarketDepthStore }) => {
          useMarketDepthStore.getState().applyUnsubscribeAck(response.symbol!);
        }
      );
    } catch (error) {
      console.error("[MarketDepth] Failed to process unsubscribe ack:", error);
    }
  }

  private isSymbolBookData(data: unknown): boolean {
    if (!data || typeof data !== "object") return false;
    const obj = data as Record<string, unknown>;
    return typeof obj.symbol === "string" && Array.isArray(obj.bars);
  }

  private clearHistoryResyncScheduling(): void {
    if (this.historyResyncTimer) {
      clearTimeout(this.historyResyncTimer);
      this.historyResyncTimer = null;
    }
    if (this.openTradesResyncTimer) {
      clearTimeout(this.openTradesResyncTimer);
      this.openTradesResyncTimer = null;
    }
    this.historyResponseMetaQueue.length = 0;
  }

  private popHistoryResponseMeta(): HistoryResponseMeta {
    const meta = this.historyResponseMetaQueue.shift();
    return (
      meta ?? {
        syncSource: "server-reload",
        batchMode: "authoritative",
      }
    );
  }

  private enqueueHistoryResponseMetaPair(meta: HistoryResponseMeta): void {
    this.historyResponseMetaQueue.push(meta, meta);
  }

  /** Debounced full closed + pending history pull to reconcile realtime rows with server. */
  private scheduleHistoryResyncFromServer(): void {
    if (this.historyResyncTimer) {
      clearTimeout(this.historyResyncTimer);
    }
    this.historyResyncTimer = setTimeout(() => {
      this.historyResyncTimer = null;
      void this.requestTradeHistory({
        syncSource: "server-reload",
        batchMode: "authoritative",
      });
    }, 400);
  }

  /**
   * Debounced open-positions pull. The blotter's only other source is the server's own
   * `VybeOnOpenTrades` push, which the ViiSync provider raises solely when the trade-book SIGNATURE
   * changes — so a fill that the provider does not mark dirty leaves the client stuck on the snapshot
   * it got at connect (empty, for a fresh account) no matter how many orders are placed.
   *
   * Deliberately a sibling of {@link scheduleHistoryResyncFromServer} rather than an extra invoke
   * inside `requestTradeHistory`: that method enqueues EXACTLY two response metas for its two history
   * invokes ({@link enqueueHistoryResponseMetaPair}), and `GetOpenTrades` answers on a different
   * message, so folding it in there would desync the meta queue against the history responses.
   */
  private scheduleOpenTradesResyncFromServer(): void {
    if (this.openTradesResyncTimer) {
      clearTimeout(this.openTradesResyncTimer);
    }
    this.openTradesResyncTimer = setTimeout(() => {
      this.openTradesResyncTimer = null;
      void this.requestOpenTrades();
    }, 400);
  }

  /** Ask the server for the authoritative open-positions snapshot; answers on `VybeOnOpenTrades`. */
  private async requestOpenTrades(): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.invoke("GetOpenTrades");
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        return;
      }
      console.error("[OpenTrades] Failed to request open trades:", error);
    }
  }

  private async requestTradeHistory(
    meta: HistoryResponseMeta = {
      syncSource: "server-reload",
      batchMode: "authoritative",
    }
  ): Promise<void> {
    if (!this.connection) {
      console.warn(
        "[TradeHistory] Cannot request trade history - not connected"
      );
      return;
    }

    try {
      // Calculate date range for the last year
      const now = new Date();
      const oneYearAgo = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
      );

      const request = {
        From: oneYearAgo.toISOString(),
        To: now.toISOString(),
        Sort: "CloseTime",
        Ascending: false,
      };

      this.enqueueHistoryResponseMetaPair(meta);

      useJournalStore.getState().addEntry({
        level: "info",
        category: "trade-history",
        message: `Sync: requesting closed + pending order history (1y window) [${meta.syncSource}/${meta.batchMode}]`,
        detail: request,
        sanitizeDetail: false,
      });

      await this.connection.invoke("GetOrderHistory", request);

      await this.connection.invoke("GetPendingOrderHistory", request);
    } catch (error) {
      if (this.intentionalDisconnect || isHubConnectionClosedError(error)) {
        return;
      }
      console.error("[TradeHistory] Failed to request trade history:", error);
    }
  }

  /** Public: full 1y closed + pending pull stamped as server reload (e.g. after browser refresh). */
  public async requestFullTradeHistoryReload(): Promise<void> {
    await this.requestTradeHistory({
      syncSource: "server-reload",
      batchMode: "authoritative",
    });
  }

  public async requestAdditionalTradeHistory(
    fromDate: string,
    toDate: string
  ): Promise<void> {
    if (!this.connection) {
      throw new Error("Not connected to SignalR");
    }

    try {
      const request = {
        From: fromDate,
        To: toDate,
        Sort: "CloseTime",
        Ascending: false,
      };

      const meta: HistoryResponseMeta = {
        syncSource: "server-request",
        batchMode: "merge",
      };
      this.enqueueHistoryResponseMetaPair(meta);

      useJournalStore.getState().addEntry({
        level: "info",
        category: "trade-history",
        message: "Requesting additional order history",
        detail: request,
        sanitizeDetail: false,
      });

      await this.connection.invoke("GetOrderHistory", request);

      await this.connection.invoke("GetPendingOrderHistory", request);
    } catch (error) {
      console.error(
        "[TradeHistory] Failed to request additional trade history:",
        error
      );
      throw error;
    }
  }

  // Trading operations methods
  public async sendOrder(orderData: unknown): Promise<void> {
    if (!this.connection || !this.isConnected()) {
      throw new Error("Not connected to SignalR");
    }
    // Investor (read-only) login: the gateway refuses every trade command. Fail here, before the round
    // trip, so no caller (order ticket, chart drag, agent tool) can put a trade on the wire.
    if (isInvestorAccount()) {
      throw new Error(INVESTOR_READ_ONLY_MESSAGE);
    }

    try {
      await this.connection.invoke("OrderSend", orderData);
    } catch (error) {
      console.error("[SignalR] Failed to send order:", error);
      throw error;
    }
  }

  public async modifyOrder(modifyData: unknown): Promise<void> {
    if (!this.connection || !this.isConnected()) {
      throw new Error("Not connected to SignalR");
    }
    // Investor (read-only) login: the gateway refuses every trade command. Fail here, before the round
    // trip, so no caller (order ticket, chart drag, agent tool) can put a trade on the wire.
    if (isInvestorAccount()) {
      throw new Error(INVESTOR_READ_ONLY_MESSAGE);
    }

    try {
      await this.connection.invoke("OrderModify", modifyData);
    } catch (error) {
      console.error("[SignalR] Failed to modify order:", error);
      throw error;
    }
  }

  public async closeOrder(closeData: VybeOrderClosePayload): Promise<void> {
    if (!this.connection || !this.isConnected()) {
      throw new Error("Not connected to SignalR");
    }
    // Investor (read-only) login: the gateway refuses every trade command. Fail here, before the round
    // trip, so no caller (order ticket, chart drag, agent tool) can put a trade on the wire.
    if (isInvestorAccount()) {
      throw new Error(INVESTOR_READ_ONLY_MESSAGE);
    }

    try {
      await this.connection.invoke("OrderClose", closeData);
    } catch (error) {
      console.error("[SignalR] Failed to close order:", error);
      throw error;
    }
  }

  public async closeOrderByHedge(
    closeData: VybeOrderClosePayload & { CloseByTicket: number }
  ): Promise<void> {
    await this.closeOrder(closeData);
  }

  private handleSymbolsUpdateData(data: unknown): void {
    try {
      // Log the structure of the data to understand what's being sent
      if (data && typeof data === "object") {
        // If it's an array, log first element
        if (Array.isArray(data) && data.length > 0) {
        }
      }

      // TODO: Once we understand the data structure, implement proper handling
      // For now, just log it for debugging
    } catch (error) {
      console.error(
        "[SymbolsUpdate] Failed to process symbols update data:",
        error
      );
    }
  }

  private resetConnectionProgress(): void {
    try {
      // Import connection progress store dynamically to avoid circular dependencies
      import(
        "../../connection-progress/stores/useConnectionProgressStore"
      ).then(({ useConnectionProgressStore }) => {
        useConnectionProgressStore.getState().reset();
      });
    } catch (error) {
      console.error(
        "[ConnectionProgress] Failed to reset progress store:",
        error
      );
    }
  }
}
